// Mirrors of the Prisma enums with human labels, so the web app never hard-codes strings.
// Keep in sync with apps/api/prisma/schema.prisma (a test in apps/api checks this).

const labels = <T extends string>(map: Record<T, string>) => ({
  values: Object.keys(map) as T[],
  label: (v: T) => map[v],
  map,
});

export const OrganizationType = labels({ customer: 'Customer', prospect: 'Prospect', vendor: 'Vendor', partner: 'Partner', internal: 'Internal' });
export const OrganizationStatus = labels({ active: 'Active', inactive: 'Inactive' });

export const TicketStatus = labels({
  new: 'New',
  open: 'Open',
  in_progress: 'In progress',
  waiting_customer: 'Waiting on customer',
  waiting_third_party: 'Waiting on third party',
  resolved: 'Resolved',
  closed: 'Closed',
});
export const OPEN_TICKET_STATUSES = ['new', 'open', 'in_progress', 'waiting_customer', 'waiting_third_party'] as const;
/** SLA clocks pause while waiting on someone else (HaloPSA "on hold" statuses). */
export const PAUSED_TICKET_STATUSES = ['waiting_customer', 'waiting_third_party'] as const;

export const TicketPriority = labels({ critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' });
// C13: a ticket's impact and urgency (high first, as the priority matrix reads).
export const TicketLevel = labels({ high: 'High', medium: 'Medium', low: 'Low' });
export const TicketType = labels({ incident: 'Incident', service_request: 'Service request', problem: 'Problem', change: 'Change', alert: 'Alert' });
// J6 (contract 1.4.0 §19): `agent` is a ticket an end user raised from the Denwen tray.
export const TicketSource = labels({ portal: 'Portal', email: 'Email', phone: 'Phone', web: 'Web', alert: 'Alert', agent: 'Denwen tray' });
export const TicketMessageKind = labels({ reply: 'Reply', internal_note: 'Internal note', system: 'System' });

export const TimeEntryStatus = labels({ draft: 'Draft', submitted: 'Submitted', approved: 'Approved', billed: 'Billed' });

export const ConfigurationStatus = labels({ active: 'Active', inactive: 'Inactive', in_repair: 'In repair', retired: 'Retired' });
export const CONFIGURATION_TYPES = [
  'Server',
  'Workstation',
  'Laptop',
  'Virtual machine',
  'Firewall',
  'Switch',
  'Router',
  'Access point',
  'Printer',
  'NAS / storage',
  'UPS',
  'Phone',
  'Mobile device',
  'Other',
] as const;

export const AgreementType = labels({
  managed_services: 'Managed services',
  block_hours: 'Block hours',
  time_and_materials: 'Time & materials',
  project: 'Project',
  retainer: 'Retainer',
});
export const AgreementStatus = labels({ draft: 'Draft', active: 'Active', expired: 'Expired', cancelled: 'Cancelled' });
export const BillingCycle = labels({ monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annually', one_off: 'One-off' });

export const ProductType = labels({ service: 'Service', labour: 'Labour', hardware: 'Hardware', software: 'Software', subscription: 'Subscription' });
export const InvoiceStatus = labels({ draft: 'Draft', sent: 'Awaiting payment', partially_paid: 'Partially paid', paid: 'Paid', void: 'Void' });
export const QuoteStatus = labels({ draft: 'Draft', sent: 'Sent', accepted: 'Accepted', declined: 'Declined', expired: 'Expired' });
export const ExpenseStatus = labels({ draft: 'Draft', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected', reimbursed: 'Reimbursed' });
export const EXPENSE_CATEGORIES = ['Travel', 'Mileage', 'Meals', 'Accommodation', 'Equipment', 'Software', 'Training', 'Office', 'Other'] as const;

export const ProjectStatus = labels({ planning: 'Planning', active: 'Active', on_hold: 'On hold', completed: 'Completed', cancelled: 'Cancelled' });
export const TaskStatus = labels({ todo: 'To do', in_progress: 'In progress', review: 'In review', done: 'Done' });
export const AppointmentType = labels({ onsite: 'Onsite', remote: 'Remote', meeting: 'Meeting', other: 'Other' });

export const EmploymentType = labels({ full_time: 'Full-time', part_time: 'Part-time', contractor: 'Contractor', temporary: 'Temporary', intern: 'Intern' });
export const EmployeeStatus = labels({ onboarding: 'Onboarding', active: 'Active', on_leave: 'On leave', offboarding: 'Offboarding', terminated: 'Terminated' });
export const LeaveStatus = labels({ pending: 'Pending', approved: 'Approved', declined: 'Declined', cancelled: 'Cancelled' });
export const TimesheetStatus = labels({ draft: 'Draft', submitted: 'Submitted', approved: 'Approved', rejected: 'Rejected' });
export const ReviewStatus = labels({ draft: 'Draft', self_review: 'Self review', manager_review: 'Manager review', completed: 'Completed' });
export const ObjectiveStatus = labels({ on_track: 'On track', at_risk: 'At risk', behind: 'Behind', completed: 'Completed' });
export const HrChecklistKind = labels({ onboarding: 'Onboarding', offboarding: 'Offboarding' });
export const EmployeeDocumentCategory = labels({ contract: 'Contract', policy: 'Policy', payslip: 'Payslip', certificate: 'Certificate', other: 'Other' });
export const JobStatus = labels({ draft: 'Draft', open: 'Open', on_hold: 'On hold', closed: 'Closed' });
export const CandidateStage = labels({ applied: 'Applied', screening: 'Screening', interview: 'Interview', offer: 'Offer', hired: 'Hired', rejected: 'Rejected' });

export const AutomationTrigger = labels({
  ticket_created: 'Ticket created',
  ticket_updated: 'Ticket updated',
  sla_response_at_risk: 'SLA response at risk',
  sla_resolution_breached: 'SLA resolution breached',
  scheduled: 'On a schedule',
  doc_expiring: 'Documentation item expiring', // M14 (track 5)
});
